"use client"

import { AnimatedButton } from "@/components/buttons/AnimatedButton"
import { OutlineToPrimaryButton } from "@/components/buttons/OutlineToPrimaryButton"
import { DefaultImage } from "@/lib/external-components"
import { createButtonStyle, toImageSrc } from "@/lib/utils"
import { HeroBannerButtonStyles, HeroBannerSlide } from "@/types/types"
import { isExternalLink } from "@/utils/ensure-protocol-helper"
import Image from "next/image"
import Link from "next/link"
import { twMerge } from "tailwind-merge"

interface HeroBannerSlideContentProps {
  slide: HeroBannerSlide
  buttonStyles?: HeroBannerButtonStyles
  className?: string
}

export const HeroBannerSlideContent = ({ slide, buttonStyles, className }: HeroBannerSlideContentProps) => {
  // Use new structure or fallback to legacy
  const imageSource = slide.background?.image || slide.backgroundImage || ""
  const bannerImageUrl = imageSource ? toImageSrc(imageSource) : ""
  const imageSrc = bannerImageUrl || DefaultImage

  // Get styles from slide
  const finalMainTextFontSize = slide.mainTextStyles?.fontSize
  const finalMainTextFontWeight = slide.mainTextStyles?.fontWeight
  const finalMainTextLineHeight = slide.mainTextStyles?.lineHeight
  const finalBackgroundHeight = slide.background?.height

  // Get button gap from buttonStyles (applies to all buttons)
  const finalButtonGap = buttonStyles?.gap

  return (
    <div
      className={twMerge("relative w-full h-[387px] sm:h-[616px] overflow-hidden", className)}
      style={{
        ...(finalBackgroundHeight && { height: finalBackgroundHeight }),
      }}
    >
      <Image
        src={imageSrc}
        alt="Hero banner"
        fill
        className="object-cover object-[center_60%] sm:object-[center_58%]"
        priority
      />

      <div className="absolute inset-0 bg-black/[0.3] z-0" />

      <div className="absolute inset-0 flex flex-col justify-start items-center pt-20 pr-0 sm:pt-[12.75rem] sm:pr-8 z-10">
        <div className="flex flex-col items-center w-full mb-0 sm:mb-[2.5rem]">
          {slide.topText && (
            <div
              className="text-white text-2xl font-black pb-[2.5rem] text-center leading-tight sm:leading-[1.625rem]"
              data-aos="fade-up"
              data-aos-duration="1000"
              data-aos-delay="0"
            >
              {slide.topText}
            </div>
          )}

          {slide.mainText && (
            <h1
              className="text-white text-4xl leading-[2.325rem] sm:text-[3.4375rem] mb-[2.5rem] sm:mb-0 font-black text-center px-4"
              style={{
                ...(finalMainTextFontSize && { fontSize: finalMainTextFontSize }),
                ...(finalMainTextFontWeight && { fontWeight: finalMainTextFontWeight }),
                ...(finalMainTextLineHeight && { lineHeight: finalMainTextLineHeight }),
              }}
              data-aos="fade-up"
              data-aos-duration="1000"
              data-aos-delay="200"
            >
              {slide.mainText}
            </h1>
          )}
        </div>

        <div
          className="flex flex-row gap-7.5 justify-center items-center w-full max-w-4xl px-4"
          style={{ ...(finalButtonGap && { gap: finalButtonGap }) }}
          data-aos="fade-up"
          data-aos-duration="1000"
          data-aos-delay="400"
        >
          {slide.listButtons &&
            slide.listButtons.length > 0 &&
            slide.listButtons.map((button, index) => {
              const buttonStyle = createButtonStyle(button, buttonStyles)
              const isExternal = isExternalLink(button.buttonLink || "")

              // Component nội dung button
              const renderButton = () => {
                switch (button.buttonType) {
                  case "outline":
                    return (
                      <OutlineToPrimaryButton
                        buttonName={button.buttonName}
                        buttonLink={button.buttonLink}
                        buttonBg={button.buttonBg || "#18AF4E"}
                        index={index}
                        buttonStyle={buttonStyle}
                      />
                    )
                  case "primary":
                  default:
                    return (
                      <AnimatedButton
                        button={button}
                        index={index}
                        totalButtons={slide.listButtons?.length || 0}
                        buttonStyle={buttonStyle}
                      />
                    )
                }
              }

              // Bọc bằng thẻ Link
              return (
                <Link
                  key={index}
                  href={button.buttonLink || "#"}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                  className="inline-flex"
                >
                  {renderButton()}
                </Link>
              )
            })}
        </div>
      </div>
    </div>
  )
}
